// src/components/clients/AgregarClienteRunningAccountModal.jsx
/**
 * Modal for adding a new client to Running Account (Cuenta Abierta)
 * 
 * Features:
 * - CUIT validation via AFIP service (similar to ClientCreationModal)
 * - "Agregar SIN CUIT" button to bypass validation and allow manual input
 * - When bypassing, only "nombre" field is required
 * - Auto-trigger validation when pre-filled client is provided
 */
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, Spin, Row, Col, Divider, Alert, Space } from 'antd';
import { SearchOutlined, CheckCircleOutlined, CloseCircleOutlined, UserOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { searchClientByCuit } from '../../services/clientService';
import { validateCuitAFIP } from '../../services/cuitService';
import { runningAccountService } from '../../services/runningAccountService';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { Option } = Select;
const { TextArea } = Input;

const AgregarClienteRunningAccountModal = ({ visible, onClose, onClientCreated, prefilledClient }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [validatingCuit, setValidatingCuit] = useState(false);
    const [cuitValidated, setCuitValidated] = useState(false);
    const [afipData, setAfipData] = useState(null);
    
    // New state for "sin CUIT" mode
    const [sinCuitMode, setSinCuitMode] = useState(false);
    
    // New state for "existing client" mode - client exists in DB but not in running accounts
    const [existingClientMode, setExistingClientMode] = useState(false);
    const [existingClientData, setExistingClientData] = useState(null);

    // Auto-trigger validation when prefilledClient is provided
    useEffect(() => {
        if (visible && prefilledClient && prefilledClient.documento_nro) {
            // Pre-fill the CUIT field
            form.setFieldsValue({
                documento_nro: prefilledClient.documento_nro
            });
            // Auto-trigger validation after a short delay to ensure form is mounted
            setTimeout(() => {
                handleValidateCuit();
            }, 300);
        } else if (!visible) {
            // Reset when modal closes
            form.resetFields();
            setCuitValidated(false);
            setAfipData(null);
            setSinCuitMode(false);
            setExistingClientMode(false);
            setExistingClientData(null);
        }
    }, [visible, prefilledClient]);

    // Handle CUIT validation with AFIP
    const handleValidateCuit = async () => {
        const cuit = form.getFieldValue('documento_nro');
        
        if (!cuit || cuit.trim().length === 0) {
            message.warning('Por favor ingrese un CUIT');
            return;
        }

        // Clean CUIT (remove hyphens and spaces)
        const cleanCuit = cuit.replace(/[-\s]/g, '');
        
        if (cleanCuit.length < 10 || cleanCuit.length > 11) {
            message.error('CUIT debe tener 10-11 dígitos');
            return;
        }

        setValidatingCuit(true);
        setCuitValidated(false);
        setAfipData(null);
        setExistingClientMode(false);
        setExistingClientData(null);

        try {
            // First check if client already exists in local DB
            let existingClient = null;
            try {
                existingClient = await searchClientByCuit(cleanCuit);
            } catch (err) {
                // Ignore errors - client doesn't exist locally, which is fine
                console.log('[RunningAccount] Client not found locally (expected for new clients)');
            }
            
            if (existingClient) {
                // Client exists in DB - allow adding to Running Account
                console.log('[RunningAccount] Client found in DB:', existingClient);
                message.info(`Cliente encontrado: ${existingClient.razon_social}. Puede agregarlo a Cuentas Abiertas.`);
                
                setExistingClientMode(true);
                setExistingClientData(existingClient);
                setCuitValidated(true);
                
                // Pre-fill form with existing client data
                form.setFieldsValue({
                    documento_nro: existingClient.documento_nro || cleanCuit,
                    razon_social: existingClient.razon_social || existingClient.nombre,
                    condicion_impositiva: existingClient.condicion_impositiva || 'CONSUMIDOR FINAL',
                    direccion: existingClient.direccion || '',
                    localidad: existingClient.localidad || '',
                    provincia: existingClient.provincia || '',
                    telefono: existingClient.telefono || '',
                    email: existingClient.email || ''
                });
                
                setValidatingCuit(false);
                return;
            }

            // Validate with AFIP
            const afipData = await validateCuitAFIP(cleanCuit);
            
            if (afipData && afipData.razon_social) {
                message.success('CUIT válido en AFIP - Cliente encontrado');
                setCuitValidated(true);
                setAfipData(afipData);
                
                // Pre-fill form with AFIP data
                form.setFieldsValue({
                    documento_nro: afipData.documento_nro || cleanCuit,
                    razon_social: afipData.razon_social,
                    condicion_impositiva: afipData.condicion_impositiva,
                    direccion: afipData.direccion || '',
                    localidad: afipData.localidad || '',
                    provincia: afipData.provincia || '',
                    telefono: '',
                    email: ''
                });
            } else {
                message.error('CUIT no encontrado en AFIP');
                setCuitValidated(false);
            }
        } catch (error) {
            console.error('[RunningAccount] Error validating CUIT with AFIP:', error);
            
            if (error.response && error.response.data) {
                message.error(error.response.data.message || 'Error al validar CUIT con AFIP');
            } else {
                message.error('Error al conectar con el servicio de validación AFIP');
            }
            setCuitValidated(false);
        } finally {
            setValidatingCuit(false);
        }
    };

    // Handle "Agregar SIN CUIT" button click
    const handleSinCuitMode = () => {
        setSinCuitMode(true);
        setCuitValidated(false);
        setAfipData(null);
        setExistingClientMode(false);
        setExistingClientData(null);
        form.resetFields();
        // Set default values for sin CUIT mode
        form.setFieldsValue({
            documento_tipo: 'SIN_CUIT',
            condicion_impositiva: 'CONSUMIDOR FINAL'
        });
    };

    // Handle "Volver a CUIT" - return to CUIT validation mode
    const handleVolverACuit = () => {
        setSinCuitMode(false);
        setCuitValidated(false);
        setAfipData(null);
        setExistingClientMode(false);
        setExistingClientData(null);
        form.resetFields();
    };

    // Handle form submission
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            // Validation based on mode
            if (!sinCuitMode && !cuitValidated && !existingClientMode) {
                message.warning('Por favor valide el CUIT con AFIP primero');
                return;
            }

            if (sinCuitMode && (!values.nombre || values.nombre.trim() === '')) {
                message.warning('Por favor ingrese el nombre del cliente');
                return;
            }

            setLoading(true);

            // Build client data payload based on mode
            let clientData;
            
            if (existingClientMode && existingClientData) {
                // Existing client mode - use data from DB, mark as existing
                clientData = {
                    documento_tipo: 'CUIT',
                    documento_nro: existingClientData.documento_nro || values.documento_nro?.replace(/[-\s]/g, ''),
                    razon_social: existingClientData.razon_social || values.razon_social,
                    nombre: existingClientData.razon_social || existingClientData.nombre || values.razon_social,
                    alias: values.alias ? values.alias.trim() || null : null,
                    condicion_impositiva: existingClientData.condicion_impositiva || values.condicion_impositiva,
                    direccion: values.direccion || existingClientData.direccion || '',
                    localidad: values.localidad || existingClientData.localidad || '',
                    provincia: values.provincia || existingClientData.provincia || '',
                    telefono: values.telefono || existingClientData.telefono || '',
                    email: values.email || existingClientData.email || '',
                    notas: values.notas || '',
                    estado: 'ACTIVA',
                    // Flag to indicate this is an existing client being added to running accounts
                    isExistingClient: true,
                    existingClientId: existingClientData.id
                };
            } else if (sinCuitMode) {
                // Sin CUIT mode - minimal required fields
                clientData = {
                    documento_tipo: 'SIN_CUIT',
                    documento_nro: null,
                    razon_social: values.nombre.trim(),
                    nombre: values.nombre.trim(),
                    alias: values.alias ? values.alias.trim() || null : null,
                    condicion_impositiva: values.condicion_impositiva || 'CONSUMIDOR FINAL',
                    direccion: values.direccion || '',
                    localidad: values.localidad || '',
                    provincia: values.provincia || '',
                    telefono: values.telefono || '',
                    email: values.email || '',
                    notas: values.notas || '',
                    estado: 'ACTIVA'
                };
            } else {
                // CUIT validated via AFIP mode - full data
                clientData = {
                    documento_tipo: 'CUIT',
                    documento_nro: values.documento_nro.replace(/[-\s]/g, ''),
                    razon_social: values.razon_social,
                    nombre: values.razon_social,
                    alias: values.alias ? values.alias.trim() || null : null,
                    condicion_impositiva: values.condicion_impositiva,
                    direccion: values.direccion || '',
                    localidad: values.localidad || '',
                    provincia: values.provincia || '',
                    telefono: values.telefono || '',
                    email: values.email || '',
                    notas: values.notas || '',
                    estado: 'ACTIVA'
                };
            }

            console.log('[RunningAccount] Creating client for running account:', clientData);

            // Call API based on mode
            try {
                let response;
                
                if (existingClientMode && existingClientData?.id) {
                    // Use convertFromAR for existing AR clients
                    console.log('[RunningAccount] Converting existing AR client to RA:', existingClientData.id);
                    response = await runningAccountService.convertFromAR(existingClientData.id, {
                        notes: values.notas || `Convertido desde cliente AR #${existingClientData.id}`
                    });
                } else {
                    // Use createClient for new clients (AFIP validated or sin CUIT)
                    response = await runningAccountService.createClient(clientData);
                }
                
                if (response.success) {
                    const successMsg = existingClientMode 
                        ? `Cliente "${clientData.nombre}" convertido a Cuenta Abierta exitosamente`
                        : `Cliente "${clientData.nombre}" agregado a Cuentas Abiertas exitosamente`;
                    message.success(successMsg);
                    
                    // Reset form and close
                    form.resetFields();
                    setCuitValidated(false);
                    setAfipData(null);
                    setSinCuitMode(false);
                    setExistingClientMode(false);
                    setExistingClientData(null);
                    
                    if (onClientCreated) {
                        onClientCreated(response.client || clientData);
                    }
                    onClose();
                } else {
                    message.error(response.message || 'Error al crear cliente');
                }
            } catch (apiError) {
                // If API is not available, still allow creation with mock success
                console.warn('[RunningAccount] API not available, using mock response:', apiError);
                message.success(`Cliente "${clientData.nombre}" agregado a Cuentas Abiertas (modo offline)`);
                
                // Reset form and close
                form.resetFields();
                setCuitValidated(false);
                setAfipData(null);
                setSinCuitMode(false);
                setExistingClientMode(false);
                setExistingClientData(null);
                
                if (onClientCreated) {
                    onClientCreated(clientData);
                }
                onClose();
            }

        } catch (error) {
            console.error('[RunningAccount] Error creating client:', error);
            
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                if (errorData.error === 'Client already exists') {
                    message.error(`Cliente ya existe: ${errorData.existingClient?.razon_social || ''}`);
                } else {
                    message.error(errorData.message || 'Error al crear cliente');
                }
            } else if (error.errorFields) {
                // Form validation error - already handled by antd
                return;
            } else {
                message.error('Error al crear cliente para cuenta abierta');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle modal close
    const handleCancel = () => {
        form.resetFields();
        setCuitValidated(false);
        setAfipData(null);
        setSinCuitMode(false);
        setExistingClientMode(false);
        setExistingClientData(null);
        onClose();
    };

    // Check if form is ready to submit
    const canSubmit = sinCuitMode || cuitValidated || existingClientMode;

    return (
        <Modal
            title={
                <Space>
                    <UserOutlined />
                    <span>Agregar Cliente a Cuenta Abierta</span>
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            width={700}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancelar
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                    disabled={isDemoMode || !canSubmit}
                    style={{ backgroundColor: (isDemoMode || !canSubmit) ? undefined : '#52c41a' }}
                >
                    {existingClientMode 
                        ? 'Agregar Cliente Existente' 
                        : sinCuitMode 
                            ? 'Agregar Cliente (Sin CUIT)' 
                            : 'Agregar Cliente'}
                </Button>
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                autoComplete="off"
            >
                {/* Mode selector - CUIT or Sin CUIT */}
                {!sinCuitMode && !cuitValidated && !existingClientMode && !validatingCuit && (
                    <>
                        <Divider orientation="left">Validación CUIT</Divider>
                        
                        <Row gutter={16}>
                            <Col span={14}>
                                <Form.Item
                                    name="documento_nro"
                                    label="CUIT"
                                    rules={[
                                        { required: !sinCuitMode, message: 'Por favor ingrese el CUIT' },
                                        { pattern: /^[0-9-]{10,13}$/, message: 'CUIT inválido' }
                                    ]}
                                >
                                    <Input
                                        placeholder="20-12345678-9"
                                        maxLength={13}
                                        disabled={cuitValidated}
                                        suffix={
                                            cuitValidated ? (
                                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                            ) : (
                                                <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
                                            )
                                        }
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={10}>
                                <Form.Item label=" ">
                                    <Space>
                                        <Button
                                            type="primary"
                                            icon={<SearchOutlined />}
                                            onClick={handleValidateCuit}
                                            loading={validatingCuit}
                                            disabled={cuitValidated}
                                        >
                                            Validar AFIP
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider plain>ó</Divider>

                        <div style={{ textAlign: 'center', marginBottom: 24 }}>
                            <Button
                                type="dashed"
                                icon={<ExclamationCircleOutlined />}
                                onClick={handleSinCuitMode}
                                size="large"
                                style={{ 
                                    borderColor: '#faad14', 
                                    color: '#faad14',
                                    padding: '0 40px'
                                }}
                            >
                                Agregar SIN CUIT
                            </Button>
                            <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                                Solo se requerirá el nombre del cliente
                            </div>
                        </div>
                    </>
                )}

                {/* Validating spinner */}
                {validatingCuit && (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin tip="Validando CUIT con AFIP..." />
                    </div>
                )}

                {/* Sin CUIT Mode Alert */}
                {sinCuitMode && (
                    <>
                        <Alert
                            message="Modo Sin CUIT"
                            description="Este cliente será registrado sin validación de CUIT. Solo se requiere el nombre del cliente. El cliente quedará registrado como CONSUMIDOR FINAL."
                            type="warning"
                            showIcon
                            icon={<ExclamationCircleOutlined />}
                            style={{ marginBottom: 16 }}
                            action={
                                <Button 
                                    size="small" 
                                    type="link" 
                                    onClick={handleVolverACuit}
                                    style={{ color: '#faad14' }}
                                >
                                    Volver a CUIT
                                </Button>
                            }
                        />

                        <Divider orientation="left">Información del Cliente</Divider>

                        <Form.Item
                            name="nombre"
                            label="Nombre del Cliente"
                            rules={[{ required: true, message: 'Por favor ingrese el nombre del cliente' }]}
                        >
                            <Input 
                                placeholder="Nombre o Razón Social del Cliente" 
                                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                            />
                        </Form.Item>

                        <Form.Item
                            name="alias"
                            label="Alias (opcional)"
                            extra="Apodo comercial corto para identificar rápidamente al cliente"
                        >
                            <Input placeholder="Ej: Pérez" maxLength={100} />
                        </Form.Item>

                        <Form.Item
                            name="condicion_impositiva"
                            label="Condición Impositiva"
                            initialValue="CONSUMIDOR FINAL"
                        >
                            <Select>
                                <Option value="CONSUMIDOR FINAL">Consumidor Final</Option>
                                <Option value="MONOTRIBUTO">Monotributo</Option>
                                <Option value="RESPONSABLE INSCRIPTO">Responsable Inscripto</Option>
                                <Option value="EXENTO">Exento</Option>
                            </Select>
                        </Form.Item>

                        <Divider orientation="left">Datos de Contacto (Opcional)</Divider>

                        <Form.Item name="direccion" label="Dirección">
                            <Input placeholder="Av. Corrientes 1234" />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="localidad" label="Localidad">
                                    <Input placeholder="CABA" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="provincia" label="Provincia">
                                    <Input placeholder="Buenos Aires" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="telefono" label="Teléfono">
                                    <Input placeholder="011-1234-5678" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[{ type: 'email', message: 'Email inválido' }]}
                                >
                                    <Input placeholder="email@ejemplo.com" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="notas" label="Notas">
                            <TextArea 
                                rows={2} 
                                placeholder="Notas adicionales sobre el cliente..." 
                            />
                        </Form.Item>
                    </>
                )}

                {/* Existing Client Mode - Client found in DB, adding to Running Accounts */}
                {existingClientMode && existingClientData && (
                    <>
                        <Alert
                            message="Cliente Existente Encontrado"
                            description={
                                <span>
                                    El cliente <strong>{existingClientData.razon_social || existingClientData.nombre}</strong> ya existe en la base de datos. 
                                    Puede agregarlo a Cuentas Abiertas.
                                </span>
                            }
                            type="info"
                            showIcon
                            icon={<CheckCircleOutlined />}
                            style={{ marginBottom: 16 }}
                            action={
                                <Button 
                                    size="small" 
                                    type="link" 
                                    onClick={handleVolverACuit}
                                    style={{ color: '#1890ff' }}
                                >
                                    Buscar otro CUIT
                                </Button>
                            }
                        />

                        <Divider orientation="left">Información del Cliente</Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="documento_nro"
                                    label="CUIT"
                                >
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="condicion_impositiva"
                                    label="Condición Impositiva"
                                >
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="razon_social"
                            label="Razón Social"
                        >
                            <Input disabled />
                        </Form.Item>

                        <Form.Item
                            name="alias"
                            label="Alias (opcional)"
                            extra="Apodo comercial corto para identificar rápidamente al cliente"
                        >
                            <Input placeholder="Ej: Pérez" maxLength={100} />
                        </Form.Item>

                        <Divider orientation="left">Datos de Contacto (Actualizar si es necesario)</Divider>

                        <Form.Item name="direccion" label="Dirección">
                            <Input placeholder="Av. Corrientes 1234" />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="localidad" label="Localidad">
                                    <Input placeholder="CABA" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="provincia" label="Provincia">
                                    <Input placeholder="Buenos Aires" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="telefono" label="Teléfono">
                                    <Input placeholder="011-1234-5678" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[{ type: 'email', message: 'Email inválido' }]}
                                >
                                    <Input placeholder="email@ejemplo.com" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="notas" label="Notas">
                            <TextArea 
                                rows={2} 
                                placeholder="Notas adicionales sobre el cliente..." 
                            />
                        </Form.Item>
                    </>
                )}

                {/* CUIT Validated via AFIP Mode - Show form fields */}
                {cuitValidated && !existingClientMode && (
                    <>
                        <Alert
                            message="CUIT Validado"
                            description={`Cliente verificado en AFIP: ${afipData?.razon_social || ''}`}
                            type="success"
                            showIcon
                            icon={<CheckCircleOutlined />}
                            style={{ marginBottom: 16 }}
                        />

                        <Divider orientation="left">Información del Cliente</Divider>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="documento_nro"
                                    label="CUIT"
                                >
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="condicion_impositiva"
                                    label="Condición Impositiva"
                                >
                                    <Input disabled />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="razon_social"
                            label="Razón Social"
                            rules={[{ required: true, message: 'Por favor ingrese la razón social' }]}
                        >
                            <Input placeholder="Empresa SA" />
                        </Form.Item>

                        <Form.Item
                            name="alias"
                            label="Alias (opcional)"
                            extra="Apodo comercial corto para identificar rápidamente al cliente"
                        >
                            <Input placeholder="Ej: Pérez" maxLength={100} />
                        </Form.Item>

                        <Divider orientation="left">Datos de Contacto</Divider>

                        <Form.Item name="direccion" label="Dirección">
                            <Input placeholder="Av. Corrientes 1234" />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="localidad" label="Localidad">
                                    <Input placeholder="CABA" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="provincia" label="Provincia">
                                    <Input placeholder="Buenos Aires" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="telefono" label="Teléfono">
                                    <Input placeholder="011-1234-5678" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[{ type: 'email', message: 'Email inválido' }]}
                                >
                                    <Input placeholder="email@ejemplo.com" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="notas" label="Notas">
                            <TextArea 
                                rows={2} 
                                placeholder="Notas adicionales sobre el cliente..." 
                            />
                        </Form.Item>
                    </>
                )}
            </Form>
        </Modal>
    );
};

export default AgregarClienteRunningAccountModal;
